import { useCallback, useEffect, useState } from "react";

export interface GalleryImage {
  src: string;
  alt: string;
}

interface PhotoGalleryProps {
  images: GalleryImage[];
  className?: string;
  layout?: "mosaic" | "strip";
}

export default function PhotoGallery({
  images,
  className = "",
  layout = "mosaic",
}: PhotoGalleryProps) {
  const [active, setActive] = useState<number | null>(null);

  const close = useCallback(() => setActive(null), []);
  const prev = useCallback(
    () => setActive((i) => (i === null ? i : (i - 1 + images.length) % images.length)),
    [images.length],
  );
  const next = useCallback(
    () => setActive((i) => (i === null ? i : (i + 1) % images.length)),
    [images.length],
  );

  useEffect(() => {
    if (active === null) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
      if (event.key === "ArrowLeft") prev();
      if (event.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, close, prev, next]);

  if (!images.length) return null;

  const gridClass =
    layout === "strip"
      ? "grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4"
      : "grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4";

  return (
    <>
      <div className={`${gridClass} ${className}`}>
        {images.map((image, index) => (
          <button
            key={image.src}
            type="button"
            onClick={() => setActive(index)}
            aria-label={`View photo: ${image.alt}`}
            className={`group relative overflow-hidden rounded-lg bg-background-200 cursor-pointer ${
              layout === "mosaic" && index === 0 ? "col-span-2 row-span-2" : "aspect-square"
            }`}
          >
            <img
              src={image.src}
              alt={image.alt}
              width={1200}
              height={900}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              decoding="async"
            />
            <span className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors"></span>
            <span className="absolute bottom-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-background-50/90 text-foreground-900 opacity-0 group-hover:opacity-100 transition-opacity">
              <i className="ri-zoom-in-line"></i>
            </span>
          </button>
        ))}
      </div>

      {active !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/92 flex flex-col items-center justify-center p-4"
          onClick={close}
        >
          <div className="w-full max-w-5xl flex items-center justify-between mb-4">
            <span className="text-background-100 text-sm font-medium">
              {active + 1} / {images.length}
            </span>
            <button
              type="button"
              onClick={close}
              aria-label="Close gallery"
              className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/25 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          <div
            className="relative w-full max-w-5xl flex items-center justify-center"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={prev}
              aria-label="Previous photo"
              className="absolute left-2 md:left-4 z-10 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/30 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-2xl"></i>
            </button>
            <div className="w-full h-[55vh] md:h-[70vh] rounded-lg overflow-hidden bg-black">
              <img
                src={images[active].src}
                alt={images[active].alt}
                width={1600}
                height={1200}
                className="w-full h-full object-contain"
                decoding="async"
              />
            </div>
            <button
              type="button"
              onClick={next}
              aria-label="Next photo"
              className="absolute right-2 md:right-4 z-10 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/30 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-2xl"></i>
            </button>
          </div>

          <p className="text-background-200 text-sm text-center mt-5 max-w-3xl">
            {images[active].alt}
          </p>
        </div>
      )}
    </>
  );
}